const { phraseResult, retryAfterMs } = require('./phrase_ideas');

// Owns one attempt, including cable/poll races, backoff, timeout and cancellation.
// Dependencies are injected so timing and stale-response behavior can be tested without a DOM.
class PhraseIdeasRequest {
  constructor({ post, get, subscribe, onState, setTimer = (fn, ms) => setTimeout(fn, ms), clearTimer = id => clearTimeout(id) }) {
    Object.assign(this, { post, get, subscribe, onState, setTimer, clearTimer });
    this.epoch = 0;
    this.timers = new Set();
  }

  cancel() {
    this.epoch++;
    this.active = false;
    this.timers.forEach(id => this.clearTimer(id));
    this.timers.clear();
    this.subscription?.unsubscribe();
    this.subscription = null;
  }

  later(fn, ms) {
    const id = this.setTimer(() => { this.timers.delete(id); fn(); }, ms);
    this.timers.add(id);
  }

  start(promptItems) {
    this.cancel();
    this.active = true;
    const epoch = this.epoch;
    const alive = () => this.active && this.epoch === epoch;
    const finish = result => {
      if (!alive()) return;
      this.cancel();
      this.onState(result);
    };
    const apply = data => {
      const result = phraseResult(data);
      if (result) finish(result);
    };
    this.onState({ loading: true });
    let requestId = null;
    const earlyMessages = new Map();
    try {
      this.subscription = this.subscribe(data => {
        if (!alive() || !data.request_id) return;
        // Static libraries can broadcast before the POST response reaches us.
        if (!requestId) { earlyMessages.set(data.request_id, data); return; }
        if (data.request_id === requestId) apply(data);
      });
    } catch (_) { /* Polling also works when cable is unavailable. */ }
    this.later(() => finish({ error: 'phrases_timed_out' }), 60000);

    const poll = async () => {
      if (!alive()) return;
      try {
        const data = await this.get(requestId);
        if (!alive()) return;
        apply(data);
      } catch (_) { /* A transient GET failure can recover on the next poll. */ }
      if (alive()) this.later(poll, 2500);
    };
    const post = async (retries = 0) => {
      try {
        const data = await this.post(promptItems);
        if (!alive()) return;
        requestId = data.request_id;
        if (!requestId) { finish({ error: 'phrases_failed' }); return; }
        if (earlyMessages.has(requestId)) apply(earlyMessages.get(requestId));
        earlyMessages.clear();
        if (alive()) poll();
      } catch (error) {
        if (!alive()) return;
        if (error.status === 429 && retries < 2) {
          this.later(() => post(retries + 1), retryAfterMs(error.retryAfter));
        } else {
          finish({ error: error.status === 429 ? 'phrases_rate_limited' : 'phrases_failed' });
        }
      }
    };
    post();
  }
}
module.exports = PhraseIdeasRequest;
